import React, { useMemo, useState } from 'react'
import { Button, Input, Popconfirm, Select, Table, Typography } from 'antd'
import {
  FilterFilled,
  SearchOutlined,
  UserAddOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { accessControlApi, type UserDto } from '../api/access-control.client'
import {
  useRoleUsersQuery,
  roleUsersQueryKey,
} from '../hooks/useRoleUsersQuery'
import { ROLES_QUERY_KEY } from '../hooks/useRolesQuery'
import { useCanManagePermissions } from '../hooks/useCanManagePermissions'
import { MY_ABILITY_QUERY_KEY } from '../../../rbac/useMyAbilityQuery'
import { useMyClientsQuery } from 'enrollment-platform/api/persons'
import { notifyError } from '../utils/notify'
import StatusBadge from './StatusBadge'
import shared from './AccessControl.styles.module.scss'
import styles from './RoleUsersTab.module.scss'
import AssignUsersModal from './AssignUsersModal'

function displayName(u: UserDto): string {
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ')
  return fullName || u.email || '—'
}

const { Text } = Typography

interface Props {
  roleId: string
}

export default function RoleUsersTab({ roleId }: Props) {
  const queryClient = useQueryClient()
  const { data: users = [], isLoading } = useRoleUsersQuery(roleId)
  const { data: clients = [], isLoading: clientsLoading } = useMyClientsQuery()
  const [search, setSearch] = useState('')
  const [companyFilter, setCompanyFilter] = useState<string | undefined>()
  const [assignOpen, setAssignOpen] = useState(false)
  const canManage = useCanManagePermissions()

  const companyOptions = useMemo(
    () => clients.map((c) => ({ value: c.id, label: c.name })),
    [clients]
  )

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return users.filter((u) => {
      const matchesSearch =
        !q ||
        `${displayName(u)} ${u.email ?? ''} ${u.cognitoUserId}`
          .toLowerCase()
          .includes(q)
      // Filter by client id (public key), not display name — distinct clients
      // can share a company name, which would otherwise cross-match.
      const matchesCompany =
        !companyFilter || u.clientPublicKey === companyFilter
      return matchesSearch && matchesCompany
    })
  }, [users, search, companyFilter])

  const {
    mutate: removeUser,
    isPending: removing,
    variables: removingUserId,
  } = useMutation({
    mutationFn: (cognitoUserId: string) => {
      const current =
        queryClient.getQueryData<UserDto[]>(roleUsersQueryKey(roleId)) ?? []
      const remaining = current
        .filter((u) => u.cognitoUserId !== cognitoUserId)
        .map((u) => u.cognitoUserId)
      return accessControlApi.syncRoleUsers(roleId, remaining)
    },
    onMutate: async (cognitoUserId: string) => {
      await queryClient.cancelQueries({ queryKey: roleUsersQueryKey(roleId) })
      const previous = queryClient.getQueryData<UserDto[]>(
        roleUsersQueryKey(roleId)
      )
      queryClient.setQueryData<UserDto[]>(
        roleUsersQueryKey(roleId),
        (old = []) => old.filter((u) => u.cognitoUserId !== cognitoUserId)
      )
      return { previous }
    },
    onError: (_err, _cognitoUserId, context) => {
      // Roll back to the pre-mutation list.
      if (context?.previous) {
        queryClient.setQueryData(roleUsersQueryKey(roleId), context.previous)
      }
      notifyError('Remove failed', 'Could not remove the user. Try again.')
    },
    onSettled: () => {
      // Reconcile with the server once the dust settles.
      queryClient.invalidateQueries({ queryKey: roleUsersQueryKey(roleId) })
      queryClient.invalidateQueries({ queryKey: ROLES_QUERY_KEY })
      // The removed user may be the current user — bust their ability cache so
      // menus/route gates reflect the revoked access now rather than after the
      // ~5-min staleTime. (Other users self-heal on next refetch/focus; the
      // server AbilityGuard is authoritative regardless.)
      queryClient.invalidateQueries({ queryKey: MY_ABILITY_QUERY_KEY })
    },
  })

  const columns: ColumnsType<UserDto> = [
    {
      title: 'User',
      key: 'user',
      render: (_, u) => (
        <div className={styles.userCell}>
          <Text className={styles.userName}>{displayName(u)}</Text>
          {u.email && <Text className={styles.userEmail}>{u.email}</Text>}
        </div>
      ),
    },
    {
      title: 'Company',
      key: 'company',
      width: 180,
      render: (_, u) =>
        u.companyName ? (
          <Text>{u.companyName}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, u) => <StatusBadge status={u.status} />,
    },
    ...(canManage
      ? [
          {
            title: '',
            key: 'actions',
            width: 80,
            align: 'right' as const,
            render: (_: unknown, u: UserDto) => (
              <Popconfirm
                title="Remove user from role?"
                onConfirm={() => removeUser(u.cognitoUserId)}
                okText="Remove"
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="text"
                  size="small"
                  danger
                  loading={removing && removingUserId === u.cognitoUserId}
                >
                  Remove
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ]

  return (
    <div className={styles.content}>
      <div className={shared.sectionHeader}>
        <div>
          <Text strong className={styles.sectionTitle}>
            Assigned users
          </Text>
          <Text type="secondary" className={styles.sectionSubtitle}>
            {canManage ? 'Manage who has this role.' : 'Users with this role.'}
          </Text>
        </div>
        {canManage && (
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setAssignOpen(true)}
          >
            Assign users
          </Button>
        )}
      </div>

      <div className={shared.filterBar}>
        <span className={shared.filterLabel}>
          <FilterFilled />
          Filter by:
        </span>
        <Select
          showSearch
          placeholder="All companies"
          options={companyOptions}
          value={companyFilter}
          onChange={(v) => setCompanyFilter(v)}
          allowClear
          onClear={() => setCompanyFilter(undefined)}
          loading={clientsLoading}
          disabled={clientsLoading}
          optionFilterProp="label"
          style={{ minWidth: 180 }}
        />
        <Input
          placeholder="Search name or email…"
          prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 320 }}
          allowClear
        />
      </div>

      <p className={shared.countText}>
        Showing <span>{filtered.length}</span> of <span>{users.length}</span>{' '}
        user{users.length !== 1 ? 's' : ''}
      </p>

      <div className={shared.tableCard}>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={filtered}
          columns={columns}
          pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small' }}
        />
      </div>

      {canManage && (
        <AssignUsersModal
          roleId={roleId}
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          alreadyAssignedIds={users.map((u) => u.cognitoUserId)}
        />
      )}
    </div>
  )
}
